import { useState, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Search, Database, FolderOpen } from 'lucide-react'
import { toast } from 'sonner'
import { projectAPI } from '@/lib/api/projects'
import { getApiErrorMessage } from '@/lib/api/errors'
import { lcaAPI } from '@/lib/api/client'
import { cn } from '@/lib/utils'
import type { ActivityWithExchanges } from '@/lib/types/project-graph'

type ExchangeMode = 'ecoinvent' | 'project'

interface SearchResult {
  name: string
  code: string
  location: string
  unit: string
  database: string
  // Biosphere-only metadata
  compartment?: string
  sub_compartment?: string
}

interface AddExchangeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  activityId: string
  activityName: string
  projectDatabase?: string
  projectActivities: ActivityWithExchanges[]
  exchangeType: 'technosphere' | 'biosphere'
  onCreated: () => void
}

export function AddExchangeDialog({
  open,
  onOpenChange,
  activityId,
  activityName,
  projectDatabase,
  projectActivities,
  exchangeType,
  onCreated,
}: AddExchangeDialogProps) {
  const [mode, setMode] = useState<ExchangeMode>('ecoinvent')
  const [amount, setAmount] = useState('1')
  const [creating, setCreating] = useState(false)

  // Ecoinvent search state
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [selected, setSelected] = useState<SearchResult | null>(null)

  // Project activity state
  const [selectedProjectActivity, setSelectedProjectActivity] =
    useState<ActivityWithExchanges | null>(null)

  // Filter out the current activity from project activity options
  const otherActivities = projectActivities.filter((a) => a.id !== activityId)

  const resetForm = () => {
    setMode('ecoinvent')
    setAmount('1')
    setQuery('')
    setResults([])
    setSelected(null)
    setSelectedProjectActivity(null)
  }

  const handleSearch = useCallback(async () => {
    if (!query.trim() || !projectDatabase) return
    setSearching(true)
    try {
      if (exchangeType === 'biosphere') {
        // Biosphere flows: hit the dedicated endpoint and read the canonical key.
        const resp = await lcaAPI.searchBiosphere({
          q: query.trim(),
          database: projectDatabase,
          limit: 30,
        })
        const data = resp.data
        const flows: Array<Record<string, unknown>> =
          data.biosphere_flows || data.flows || data.results || []
        const merged: SearchResult[] = flows.map((f) => ({
          name: String(f.name ?? ''),
          code: String(f.code ?? ''),
          location: String(f.compartment ?? '-'),
          unit: String(f.unit ?? ''),
          database: projectDatabase,
          compartment: f.compartment != null ? String(f.compartment) : undefined,
          sub_compartment: f.sub_compartment != null ? String(f.sub_compartment) : undefined,
        }))
        setResults(merged)
      } else {
        // Technosphere: backend returns { ecoinvent_activities, custom_activities }.
        // Older code read `data.results || data.activities` and got nothing.
        const resp = await lcaAPI.searchActivities({
          q: query.trim(),
          database: projectDatabase,
          limit: 30,
        })
        const data = resp.data
        type RawActivity = Partial<SearchResult> & { database?: string }
        const merged: SearchResult[] = [
          ...((data.ecoinvent_activities as RawActivity[] | undefined) || []).map(
            (a) => ({ ...a, database: a.database || projectDatabase }) as SearchResult,
          ),
          ...((data.custom_activities as RawActivity[] | undefined) || []).map(
            (a) => ({ ...a, database: a.database || 'custom' }) as SearchResult,
          ),
        ]
        setResults(merged)
      }
      setSelected(null)
    } catch {
      toast.error('Search failed')
    } finally {
      setSearching(false)
    }
  }, [query, projectDatabase, exchangeType])

  const handleCreate = async () => {
    const numAmount = parseFloat(amount)
    if (isNaN(numAmount) || numAmount <= 0) {
      toast.error('Amount must be a positive number')
      return
    }

    setCreating(true)
    try {
      if (mode === 'ecoinvent' && selected) {
        // Biosphere flows route through the same /exchanges/ecoinvent endpoint
        // but with input_biosphere_code instead of input_activity_code. Mixing
        // them up makes the server take the technosphere branch, fail the
        // Neo4j activity lookup, and leave the activity in a state that
        // crashes the right-panel render.
        const payload =
          exchangeType === 'biosphere'
            ? {
                amount: numAmount,
                exchange_type: 'biosphere',
                input_database: selected.database || projectDatabase!,
                input_biosphere_code: selected.code,
              }
            : {
                amount: numAmount,
                exchange_type: exchangeType,
                input_database: selected.database || projectDatabase!,
                input_activity_code: selected.code,
              }
        await projectAPI.createEcoinventExchange(activityId, payload)
        toast.success(
          exchangeType === 'biosphere' ? 'Biosphere flow added' : 'Ecoinvent exchange added',
        )
      } else if (mode === 'project' && selectedProjectActivity) {
        await projectAPI.createCustomExchange(activityId, {
          name: selectedProjectActivity.name,
          amount: numAmount,
          unit: selectedProjectActivity.unit,
          exchange_type: exchangeType,
          input_activity_code: selectedProjectActivity.code,
          input_database: 'custom',
        })
        toast.success('Project exchange added')
      } else {
        toast.error('Select an activity first')
        setCreating(false)
        return
      }
      resetForm()
      onOpenChange(false)
      onCreated()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to create exchange'))
    } finally {
      setCreating(false)
    }
  }

  const canCreate = mode === 'ecoinvent' ? !!selected : !!selectedProjectActivity

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!creating) {
          onOpenChange(v)
          if (!v) resetForm()
        }
      }}
    >
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-sm">
            Add {exchangeType === 'technosphere' ? 'Input' : 'Emission'} to {activityName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2 flex-1 overflow-hidden flex flex-col">
          {/* Mode toggle — hidden for biosphere (project activities are not biosphere flows) */}
          {exchangeType !== 'biosphere' && (
            <div className="flex gap-1 p-0.5 bg-muted/50 rounded-md">
              <button
                onClick={() => setMode('ecoinvent')}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 h-7 text-xs rounded transition-colors',
                  mode === 'ecoinvent'
                    ? 'bg-background shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Database className="h-3 w-3" />
                Ecoinvent
              </button>
              <button
                onClick={() => setMode('project')}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 h-7 text-xs rounded transition-colors',
                  mode === 'project'
                    ? 'bg-background shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <FolderOpen className="h-3 w-3" />
                Project
              </button>
            </div>
          )}

          {/* Ecoinvent / biosphere mode: search */}
          {mode === 'ecoinvent' && (
            <div className="space-y-2 flex-1 overflow-hidden flex flex-col">
              <div className="flex gap-1.5">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder={
                      exchangeType === 'biosphere'
                        ? 'Search biosphere flows...'
                        : 'Search ecoinvent...'
                    }
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="h-8 pl-7 text-xs"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs"
                  onClick={handleSearch}
                  disabled={searching || !query.trim()}
                >
                  {searching ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Search'}
                </Button>
              </div>

              {/* Results list */}
              {results.length > 0 && (
                <div className="flex-1 overflow-y-auto border rounded-md divide-y divide-border min-h-0">
                  {results.map((r) => (
                    <button
                      key={r.code}
                      onClick={() => setSelected(r)}
                      className={cn(
                        'w-full text-left px-2.5 py-2 text-xs hover:bg-accent/50 transition-colors',
                        selected?.code === r.code && 'bg-accent',
                      )}
                    >
                      <p className="font-medium truncate">{r.name}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {r.location} · {r.unit} · {r.code.slice(0, 8)}...
                      </p>
                    </button>
                  ))}
                </div>
              )}
              {selected && (
                <div className="p-2 rounded-md bg-muted/50 border text-xs">
                  <p className="font-medium">{selected.name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {selected.location} · {selected.unit}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Project mode: dropdown */}
          {mode === 'project' && (
            <div className="space-y-2">
              {otherActivities.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  No other activities in this project to link
                </p>
              ) : (
                <div className="border rounded-md divide-y divide-border max-h-48 overflow-y-auto">
                  {otherActivities.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => setSelectedProjectActivity(a)}
                      className={cn(
                        'w-full text-left px-2.5 py-2 text-xs hover:bg-accent/50 transition-colors',
                        selectedProjectActivity?.id === a.id && 'bg-accent',
                      )}
                    >
                      <p className="font-medium truncate">{a.name}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {a.location || 'GLO'} · {a.unit}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Amount */}
          <div className="space-y-1.5">
            <Label htmlFor="exc-amount" className="text-xs">
              Amount
            </Label>
            <Input
              id="exc-amount"
              type="number"
              step="any"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onOpenChange(false)
              resetForm()
            }}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleCreate} disabled={creating || !canCreate}>
            {creating && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Add Exchange
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
